/**
 * Advanced CPR + Pivot Strategy
 *
 * Professional CPR (Central Pivot Range) analysis system:
 *   CPR Engine → Pivot Engine → Day Classification → Virgin CPR
 *   → Breakout/Retest/Failure → Confluence Score → Signal Engine
 *   → Dashboard → Alerts
 *
 * Modules live in ./cpr/ and are intentionally decoupled (§34) so they
 * can be reused by future indicators (confluence / entry-exit engines).
 *
 * Non-repainting (§28):
 *   - Levels are computed ONLY from the previous completed session's HLC
 *     (the last candle's session is treated as in-progress).
 *   - Signals, markers and alert events are emitted exclusively for
 *     CLOSED bars (index ≤ n-2); the forming bar never produces them.
 *   - Display rows (price, VWAP, virgin status) are live state — they
 *     describe "now", never history.
 *   - Virgin status is monotonic (VIRGIN → MITIGATED only).
 *
 * Alert events are emitted as metadata.alerts and surfaced by the
 * ChartPanel alert processor (deduped, once per qualifying event).
 */

import { CandleData } from "../../types/chart.types";
import {
  IndicatorBand, IndicatorDefinition, IndicatorMarker, IndicatorParam,
  IndicatorPriceLine, PlotData,
} from "../types/indicator.types";
import {
  aggregateSessions, computeCpr, computePivots, currentSession,
  previousCompletedSession,
} from "./cpr/cpr-engine";
import {
  classifyBias, classifyGap, classifyOpenLocation, classifyPosition,
  classifyWidth, dayConditionFromWidth, priceVsCpr,
} from "./cpr/classification";
import {
  buildSessionVwap, marketRegime, marketStructure, openingRange,
} from "./cpr/market-context";
import { virginCprStatus } from "./cpr/virgin-cpr";
import { runBreakoutEngine } from "./cpr/breakout";
import {
  confluenceScore, overallMarketCondition, ScoreContext, vwapConfluenceText,
} from "./cpr/confluence";
import { runSignalEngine } from "./cpr/signal-engine";
import {
  CprAlertEvent, CprDashboard, CprRow, RowColor, SessionAgg, WidthType,
} from "./cpr/types";

// ─── Formatting ───────────────────────────────────────────────────

const fmt = (v: number): string =>
  v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtLevel = (v: number): string =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 2 });

const widthTypeColor = (t: WidthType): RowColor =>
  t === "NARROW" ? "warn" : t === "WIDE" ? "purple" : "val";

// ─── Parameters (§30) ─────────────────────────────────────────────

const PARAMS: IndicatorParam[] = [
  // CPR Display
  { key: "showCPR", label: "Show CPR (TC/BC)", type: "boolean", default: true, group: "CPR Display" },
  { key: "showPivot", label: "Show Pivot", type: "boolean", default: true, group: "CPR Display" },
  { key: "showR1R3", label: "Show R1-R3", type: "boolean", default: true, group: "CPR Display" },
  { key: "showS1S3", label: "Show S1-S3", type: "boolean", default: true, group: "CPR Display" },
  { key: "showCprZone", label: "Show CPR Zone", type: "boolean", default: true, group: "CPR Display" },
  {
    key: "displayMode", label: "Display", type: "select", default: "Both", group: "CPR Display",
    options: [
      { label: "Lines", value: "Lines" },
      { label: "Zones", value: "Zones" },
      { label: "Both", value: "Both" },
    ],
  },
  { key: "showLabels", label: "Show Labels", type: "boolean", default: true, group: "CPR Display" },

  // Levels
  {
    key: "pivotType", label: "Pivot Type", type: "select", default: "Standard", group: "Levels",
    options: [
      { label: "Standard", value: "Standard" },
      { label: "Fibonacci", value: "Fibonacci" },
      { label: "Camarilla", value: "Camarilla" },
      { label: "Woodie", value: "Woodie" },
      { label: "Classic", value: "Classic" },
    ],
  },
  { key: "showVirginCpr", label: "Show Virgin CPR", type: "boolean", default: true, group: "Levels" },
  { key: "showPdh", label: "Show PDH", type: "boolean", default: false, group: "Levels" },
  { key: "showPdc", label: "Show PDC", type: "boolean", default: false, group: "Levels" },
  { key: "showPdl", label: "Show PDL", type: "boolean", default: false, group: "Levels" },
  { key: "showWeeklyCpr", label: "Show Weekly CPR", type: "boolean", default: false, group: "Levels" },
  { key: "showMonthlyCpr", label: "Show Monthly CPR", type: "boolean", default: false, group: "Levels" },

  // Classification
  { key: "widthLookback", label: "CPR Width Lookback", type: "number", default: 20, min: 5, max: 250, step: 1, group: "Classification" },
  { key: "narrowThreshold", label: "Narrow Threshold", type: "number", default: 0.75, min: 0.1, max: 3, step: 0.05, group: "Classification" },
  { key: "wideThreshold", label: "Wide Threshold", type: "number", default: 1.25, min: 0.1, max: 3, step: 0.05, group: "Classification" },
  { key: "gapThresholdPct", label: "Gap Threshold %", type: "number", default: 0.1, min: 0.01, max: 5, step: 0.01, group: "Classification" },
  { key: "openingRangeMin", label: "Opening Range (min)", type: "number", default: 15, min: 1, max: 240, step: 1, group: "Classification" },

  // Signals
  { key: "showBreakoutSignals", label: "Show Breakout Signals", type: "boolean", default: true, group: "Signals" },
  { key: "showRetestSignals", label: "Show Retest Signals", type: "boolean", default: true, group: "Signals" },
  { key: "showFailedBreakout", label: "Show Failed Breakout", type: "boolean", default: true, group: "Signals" },
  { key: "showSetups", label: "Show Long/Short Setups", type: "boolean", default: false, group: "Signals" },
  { key: "useVwapConfirm", label: "Use VWAP Confirmation", type: "boolean", default: true, group: "Signals" },
  { key: "useVolumeConfirm", label: "Use Volume Confirmation", type: "boolean", default: true, group: "Signals" },
  { key: "useStructureConfirm", label: "Use Market Structure Confirmation", type: "boolean", default: true, group: "Signals" },
  { key: "minScore", label: "Signal Score Minimum", type: "number", default: 70, min: 0, max: 100, step: 5, group: "Signals" },

  // Strategy SL/TP
  {
    key: "longSlSource", label: "Long Stop-Loss", type: "select", default: "CPR Bottom", group: "Strategy SL/TP",
    options: [
      { label: "CPR Bottom", value: "CPR Bottom" },
      { label: "CPR Top", value: "CPR Top" },
      { label: "ATR", value: "ATR" },
      { label: "Previous Swing Low", value: "Previous Swing Low" },
    ],
  },
  {
    key: "shortSlSource", label: "Short Stop-Loss", type: "select", default: "CPR Top", group: "Strategy SL/TP",
    options: [
      { label: "CPR Top", value: "CPR Top" },
      { label: "CPR Bottom", value: "CPR Bottom" },
      { label: "ATR", value: "ATR" },
      { label: "Previous Swing High", value: "Previous Swing High" },
    ],
  },
  {
    key: "longTpSource", label: "Long Target", type: "select", default: "R1", group: "Strategy SL/TP",
    options: [
      { label: "R1", value: "R1" }, { label: "R2", value: "R2" }, { label: "R3", value: "R3" },
      { label: "Fixed RR", value: "Fixed RR" }, { label: "ATR Multiple", value: "ATR Multiple" },
    ],
  },
  {
    key: "shortTpSource", label: "Short Target", type: "select", default: "S1", group: "Strategy SL/TP",
    options: [
      { label: "S1", value: "S1" }, { label: "S2", value: "S2" }, { label: "S3", value: "S3" },
      { label: "Fixed RR", value: "Fixed RR" }, { label: "ATR Multiple", value: "ATR Multiple" },
    ],
  },
  { key: "rrMultiple", label: "Fixed RR Multiple", type: "number", default: 2, min: 0.5, max: 10, step: 0.5, group: "Strategy SL/TP" },
  { key: "slAtrMult", label: "SL ATR Multiple", type: "number", default: 1.5, min: 0.5, max: 10, step: 0.5, group: "Strategy SL/TP" },
  { key: "tpAtrMult", label: "Target ATR Multiple", type: "number", default: 3, min: 0.5, max: 20, step: 0.5, group: "Strategy SL/TP" },

  // Confluence
  { key: "confluencePct", label: "S/R Confluence Proximity %", type: "number", default: 0.1, min: 0.01, max: 2, step: 0.01, group: "Confluence" },

  // Styles
  { key: "colorCprZone", label: "CPR Zone Color", type: "color", default: "#2962FF", group: "Styles" },
  { key: "colorPivot", label: "Pivot Color", type: "color", default: "#FF9800", group: "Styles" },
  { key: "colorResistance", label: "Resistance Color", type: "color", default: "#26A69A", group: "Styles" },
  { key: "colorSupport", label: "Support Color", type: "color", default: "#EF5350", group: "Styles" },
  { key: "colorWeekly", label: "Weekly CPR Color", type: "color", default: "#AB47BC", group: "Styles" },
  { key: "colorMonthly", label: "Monthly CPR Color", type: "color", default: "#78909C", group: "Styles" },
  { key: "colorPrevious", label: "Previous Day Color", type: "color", default: "#546E7A", group: "Styles" },
];

const DEFAULTS: Record<string, any> = Object.fromEntries(
  PARAMS.map(prm => [prm.key, prm.default] as const),
);

// ─── Calculate ────────────────────────────────────────────────────

const calculate = (candles: CandleData[], params: Record<string, any>): PlotData[] => {
  const p: Record<string, any> = { ...DEFAULTS, ...params };

  const empty = (): PlotData[] => [{
    key: "levels",
    data: [],
    markers: [],
    priceLines: [],
    bands: [],
    metadata: { dashboard: null, alerts: [] },
  }];

  const n = candles.length;
  if (n < 3) return empty();

  const lastClosedIdx = n - 2;
  const liveIdx = n - 1;

  const sessions = aggregateSessions(candles, "Daily");
  if (sessions.length < 2) return empty();

  const cur = currentSession(sessions);
  const prev = previousCompletedSession(sessions);
  if (!cur || !prev) return empty();

  const prevSource = sessions[sessions.length - 2]; // HLC source for today's levels
  const curSession = sessions[sessions.length - 1];

  // ── §1/§7: CPR + pivot ladder from the previous completed session ──
  const cpr = computeCpr(prevSource.high, prevSource.low, prevSource.close);
  const levels = computePivots(p.pivotType, prevSource.high, prevSource.low, prevSource.close);

  // ── §4: previous CPR (source = session before the level source) ──
  const positionAvailable = sessions.length >= 3;
  const prevCprSource = positionAvailable ? sessions[sessions.length - 3] : null;
  const prevCpr = prevCprSource
    ? computeCpr(prevCprSource.high, prevCprSource.low, prevCprSource.close)
    : null;
  const position = prevCpr ? classifyPosition(cpr, prevCpr) : "OVERLAPPING CPR";

  // ── §3: width classification vs historical CPR widths ──
  const priorWidths: number[] = [];
  for (let k = 0; k <= sessions.length - 3; k++) {
    const s = sessions[k];
    priorWidths.push(computeCpr(s.high, s.low, s.close).width);
  }
  const widthClass = classifyWidth(
    cpr.width, priorWidths, p.widthLookback, p.narrowThreshold, p.wideThreshold,
  );

  // ── live display state ──
  const liveClose = candles[liveIdx].close;
  const vsCprLive = priceVsCpr(liveClose, cpr);
  const gap = classifyGap(curSession.open, prevSource.close, p.gapThresholdPct);
  const openLoc = classifyOpenLocation(curSession.open, cpr, levels);
  const or = openingRange(candles, curSession, p.openingRangeMin);

  const vwap = buildSessionVwap(candles, curSession);
  const liveVwap = vwap.get(liveIdx);
  const vwapState: "ABOVE" | "BELOW" | "N/A" =
    liveVwap == null ? "N/A" : liveClose >= liveVwap ? "ABOVE" : "BELOW";

  const bias = classifyBias(position, vsCprLive, vwapState);
  const dayCondition = dayConditionFromWidth(widthClass.type);

  const structure = marketStructure(candles, lastClosedIdx, 20);
  const regime = marketRegime(
    candles, lastClosedIdx, cpr.top, cpr.bottom, 20,
  );
  const overall = overallMarketCondition(widthClass.type, bias, regime);

  // ── §6: virgin CPR (origin = previous session's HLC source) ──
  const virgin = virginCprStatus(
    candles, sessions, sessions.length - 2, cpr.top, cpr.bottom, lastClosedIdx,
  );

  // ── §16: VWAP confluence at the last closed bar ──
  const scoreCtx: ScoreContext = {
    widthType: widthClass.type,
    position,
    vwap,
    structure,
    vwapState: vwap.get(lastClosedIdx) != null
      ? (candles[lastClosedIdx].close >= (vwap.get(lastClosedIdx) as number) ? "ABOVE" : "BELOW")
      : "N/A",
    vsCpr: priceVsCpr(candles[lastClosedIdx].close, cpr),
  };
  const score = confluenceScore(candles, lastClosedIdx, scoreCtx, bias);
  const vwapConf = vwapConfluenceText(candles, lastClosedIdx, liveClose, cpr, vwap);

  // ── §12–14: breakout / retest / failure + level touches ──
  const hasClosedInSession = curSession.firstIdx <= lastClosedIdx;
  const breakout = hasClosedInSession
    ? runBreakoutEngine({
        candles, session: curSession, lastClosedIdx, cpr, levels, vwap,
        confirm: { useVolume: p.useVolumeConfirm, useVwap: p.useVwapConfirm },
        flags: {
          showBreakout: p.showBreakoutSignals,
          showRetest: p.showRetestSignals,
          showFailed: p.showFailedBreakout,
        },
      })
    : { markers: [], alerts: [], bullState: 0 as const, bearState: 0 as const, levelTouched: { cpr: false, r1: false, r2: false, r3: false, s1: false, s2: false, s3: false } };

  // ── §19–21: strategy-mode setups ──
  const signals = hasClosedInSession
    ? runSignalEngine({
        candles, session: curSession, lastClosedIdx, cpr, levels,
        widthType: widthClass.type, position, regime,
        params: {
          showSetups: p.showSetups,
          useVwap: p.useVwapConfirm,
          useVolume: p.useVolumeConfirm,
          useStructure: p.useStructureConfirm,
          minScore: p.minScore,
          longSlSource: p.longSlSource,
          shortSlSource: p.shortSlSource,
          longTpSource: p.longTpSource,
          shortTpSource: p.shortTpSource,
          rrMultiple: p.rrMultiple,
          slAtrMult: p.slAtrMult,
          tpAtrMult: p.tpAtrMult,
        },
      })
    : { markers: [], alerts: [], setup: null, rangeActive: false, scoreAtClose: null };

  // ── alerts (§6 mitigation + engines) ──
  const alerts: CprAlertEvent[] = [...breakout.alerts, ...signals.alerts];
  if (virgin.state === "MITIGATED" && virgin.firstTouch && virgin.firstTouch.closed) {
    alerts.push({
      time: virgin.firstTouch.time,
      event: "Virgin CPR Mitigation",
      price: candles[virgin.firstTouch.idx].close,
      direction: "bear",
    });
  }

  // ── price lines (§9, §23) ──
  const priceLines: IndicatorPriceLine[] = [];
  const fromTime = curSession.start;
  const addLine = (
    id: string, price: number, color: string, title: string,
    lineStyle: number, lineWidth: number,
  ) => {
    priceLines.push({
      id, price, color, title, lineStyle, lineWidth,
      axisLabelVisible: !!p.showLabels, lineVisible: true, fromTime,
    });
  };

  if (p.showR1R3) {
    addLine("r3", levels.r3, p.colorResistance, "R3", 0, 1);
    addLine("r2", levels.r2, p.colorResistance, "R2", 0, 1);
    addLine("r1", levels.r1, p.colorResistance, "R1", 0, 1);
  }
  if (p.showS1S3) {
    addLine("s1", levels.s1, p.colorSupport, "S1", 0, 1);
    addLine("s2", levels.s2, p.colorSupport, "S2", 0, 1);
    addLine("s3", levels.s3, p.colorSupport, "S3", 0, 1);
  }
  if (p.showPivot) addLine("pivot", levels.pivot, p.colorPivot, "PIVOT", 0, 2);

  const mode = p.displayMode as string;
  if (p.showCPR && mode !== "Zones") {
    addLine("tc", cpr.top, p.colorCprZone, "TC", 0, 2);
    addLine("bc", cpr.bottom, p.colorCprZone, "BC", 0, 2);
  }
  if (p.showPdh) addLine("pdh", prevSource.high, p.colorPrevious, "PDH", 2, 1);
  if (p.showPdc) addLine("pdc", prevSource.close, p.colorPrevious, "PDC", 2, 1);
  if (p.showPdl) addLine("pdl", prevSource.low, p.colorPrevious, "PDL", 2, 1);

  // ── bands (§10, §25) ──
  const bands: IndicatorBand[] = [];
  const zoneVisible = (p.showCprZone || p.showVirginCpr) && mode !== "Lines";
  if (zoneVisible) {
    const virginLabel = virgin.state === "VIRGIN" ? "VIRGIN CPR ✓" : "VIRGIN CPR ✕";
    bands.push({
      id: "cpr-zone",
      top: cpr.top,
      bottom: cpr.bottom,
      fill: p.colorCprZone + "33",
      border: p.colorCprZone,
      fromTime,
      dotted: !!p.showVirginCpr && virgin.state === "MITIGATED",
      lineWidth: 1,
      label: p.showVirginCpr ? virginLabel : "CPR",
      labelColor: p.showVirginCpr
        ? virgin.state === "VIRGIN" ? "#00B450" : "#DC3232"
        : undefined,
      labelAlign: "left",
    });
  }

  let mtfConfluence = false;
  const buildMtfBand = (
    tf: "Weekly" | "Monthly", enabled: boolean, color: string, label: string,
  ) => {
    if (!enabled) return;
    const ss = aggregateSessions(candles, tf);
    if (ss.length < 2) return;
    const src = ss[ss.length - 2];
    const active = ss[ss.length - 1];
    const mc = computeCpr(src.high, src.low, src.close);
    bands.push({
      id: `${tf.toLowerCase()}-cpr`,
      top: mc.top,
      bottom: mc.bottom,
      fill: color + "22",
      border: color,
      fromTime: active.start,
      lineWidth: 1,
      label,
      labelColor: color,
      labelAlign: "left" as const,
    });
    if (tf === "Weekly") {
      mtfConfluence = cpr.bottom <= mc.top && cpr.top >= mc.bottom;
    }
  };
  buildMtfBand("Weekly", p.showWeeklyCpr, p.colorWeekly, "WEEKLY CPR");
  buildMtfBand("Monthly", p.showMonthlyCpr, p.colorMonthly, "MONTHLY CPR");

  // ── markers (§12–14, §19/20) ──
  const markers: IndicatorMarker[] = [...breakout.markers, ...signals.markers]
    .sort((a, b) => a.time - b.time);

  // ── dashboard (§22) ──
  const rows: CprRow[] = [];
  const push = (k: string, v: string, c: RowColor, section?: string) =>
    rows.push(section ? { k, v, c, section } : { k, v, c });

  // Block 1 — CPR identity
  push("CPR Type", widthClass.type, widthTypeColor(widthClass.type));
  push("CPR Position", positionAvailable ? position : "N/A",
    position === "HIGHER CPR" ? "up" : position === "LOWER CPR" ? "down" : "txt");
  push("CPR Width", cpr.width.toFixed(2), "val");
  push("Width %", `${cpr.widthPct.toFixed(3)}%`, "val");

  // Block 2 — day classification
  push("Day Condition", dayCondition,
    dayCondition === "POTENTIAL EXPANSION" ? "warn"
    : dayCondition === "POTENTIAL RANGE" ? "cyan" : "val", "Day");
  push("Overall Condition", overall,
    overall === "POTENTIAL BULLISH EXPANSION" ? "up"
    : overall === "POTENTIAL BEARISH EXPANSION" ? "down"
    : overall === "POTENTIAL RANGE / LOW CONVICTION" ? "cyan" : "val");
  push("Market Bias", bias, bias === "BULLISH" ? "up" : bias === "BEARISH" ? "down" : "txt");
  push("Market Regime", regime,
    regime === "TRENDING" ? "up" : regime === "EXPANSION" ? "warn"
    : regime === "CHOPPY" ? "down" : regime === "TRANSITION" ? "purple" : "cyan");

  // Block 3 — live session state
  push("VWAP", vwapState === "N/A" ? "N/A" : `${vwapState}${liveVwap != null ? ` ${fmtLevel(liveVwap)}` : ""}`,
    vwapState === "ABOVE" ? "up" : vwapState === "BELOW" ? "down" : "txt", "Session");
  push("Price", vsCprLive,
    vsCprLive === "ABOVE CPR" ? "up" : vsCprLive === "BELOW CPR" ? "down" : "txt");
  push("Open", openLoc, "val");
  push("Gap", gap, gap === "GAP UP" ? "up" : gap === "GAP DOWN" ? "down" : "txt");
  push("Opening Range", `${fmtLevel(or.high)} – ${fmtLevel(or.low)}`, "txt");
  if (p.showVirginCpr) {
    push("Previous CPR",
      virgin.state === "VIRGIN" ? "VIRGIN ✓" : virgin.state === "MITIGATED" ? "MITIGATED ✕" : "N/A",
      virgin.state === "VIRGIN" ? "up" : virgin.state === "MITIGATED" ? "down" : "txt");
  }
  if (vwapConf) {
    push("VWAP Confluence", vwapConf,
      vwapConf.startsWith("BULLISH") ? "up" : vwapConf.startsWith("BEARISH") ? "down" : "warn");
  }

  // Block 4 — confluence score (§18)
  score.components.forEach((comp, i) => {
    push(comp.key, `${comp.score}/${comp.max}`,
      comp.score >= comp.max * 0.7 ? "up" : "val",
      i === 0 ? "Confluence Score" : undefined);
  });
  push("Total", `${score.total}/${score.max}`,
    score.total >= 80 ? "up" : score.total >= 60 ? "warn" : "down");
  if (mtfConfluence) push("MTF", "DAILY + WEEKLY CPR CONFLUENCE", "warn");

  // Block 5 — levels
  if (p.showR1R3) {
    push("R1", fmtLevel(levels.r1), "down", "Levels");
    push("R2", fmtLevel(levels.r2), "down");
    push("R3", fmtLevel(levels.r3), "down");
  } else {
    push("Levels", "R1-R3 hidden", "txt", "Levels");
  }
  push("CPR Top", fmtLevel(cpr.top), "cyan");
  push("Pivot", fmtLevel(levels.pivot), "val");
  push("CPR Bottom", fmtLevel(cpr.bottom), "cyan");
  if (p.showS1S3) {
    push("S1", fmtLevel(levels.s1), "up");
    push("S2", fmtLevel(levels.s2), "up");
    push("S3", fmtLevel(levels.s3), "up");
  }

  // Block 6 — setups / range (§19–21)
  if (signals.setup) {
    const s = signals.setup;
    push(s.side === "LONG" ? "Long Setup" : "Short Setup",
      `Entry ${fmt(s.entry)}`,
      s.side === "LONG" ? "up" : "down", "Setup");
    push("Stop-Loss", `${fmt(s.sl)} (${s.slSource})`, "down");
    push("Target", `${fmt(s.tp)} (${s.tpSource})`, "up");
  } else if (signals.rangeActive) {
    push("Range Setup", "POTENTIAL RANGE", "cyan", "Setup");
    push("Range Ideas", "R1 rej → short · S1 rej → long", "txt");
  }

  const dashboard: CprDashboard = {
    title: "CPR MARKET ANALYSIS",
    rows,
    cprTop: cpr.top,
    pivot: levels.pivot,
    cprBottom: cpr.bottom,
    levels: { r1: levels.r1, r2: levels.r2, r3: levels.r3, s1: levels.s1, s2: levels.s2, s3: levels.s3 },
    width: cpr.width,
    widthPct: cpr.widthPct,
    widthType: widthClass.type,
    positionType: position,
    bias,
    regime,
    dayCondition,
    vwap: liveVwap ?? null,
    vwapState,
    score,
    virginState: virgin.state,
    setup: signals.setup,
    mtfConfluence,
    srProximityPct: p.confluencePct,
  };

  return [{
    key: "levels",
    data: [],
    markers,
    priceLines,
    bands,
    metadata: { dashboard, alerts },
  }];
};

// ─── Definition ───────────────────────────────────────────────────

export const advancedCprPivotIndicator: IndicatorDefinition = {
  id: "advanced_cpr_pivot",
  name: "Advanced CPR + Pivot Strategy",
  shortName: "CPR",
  category: "advanced",
  pane: "overlay",
  params: PARAMS,
  plots: [
    { key: "levels", label: "CPR Levels", color: "#2962FF", style: "line", lineWidth: 1 },
  ],
  calculate,
};

/** Dashboard/row types re-exported for the overlay component. */
export type { CprDashboard, CprRow } from "./cpr/types";
